import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { defaultpageCount } from 'utils/constants';
import { toast } from 'react-toastify';
import { messageErrors, formatDateNotTime } from 'utils/utils';
import { Table } from 'flowbite-react';
import ActionTable from 'components/table/actionTable';
import PaginateTable from 'components/table/paginate';
import ConfirmModal from 'components/modal/confirmModal';
import SpinnerComponent from 'components/spinner';
import Select from 'components/select';
import EmptyState from 'components/commonComponent/emptyState';
import NoResult from 'components/commonComponent/noResult';
import { HiOutlineTrash } from 'react-icons/hi';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import { assignVendorToOrg, getMasterVendors, getOrganizationVendorAssignments, unassignVendor } from 'api/vendorApi';
import { getOrganizations } from 'api/organizationApi';

const assignmentEmpty = require('../../assets/image/svg/document-text.svg').default || '';

const VendorAssignment = () => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalEntities, setTotalEntities] = useState(0);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [idDelete, setIdDelete] = useState('');
  const [pageCount, setPageCount] = useState(defaultpageCount);
  const [vendorSelect, setVendorSelect] = useState<any>(null);
  const [orgSelect, setOrgSelect] = useState<any>(null);

  const { data: vendorData } = useQuery(['getMasterVendorsForAssign'], getMasterVendors, {
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { data: organizationData }: any = useQuery(['getOrganizationsForAssign'], getOrganizations, {
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const fetchAssignments = () => {
    const queryFilter: any = {
      limit: pageCount,
      searchQuery: searchValue,
    };
    if (currentPage > 0) {
      queryFilter.page = currentPage;
    }
    return getOrganizationVendorAssignments(queryFilter).then((res: any) => {
      return res;
    }).catch((error: any) => {
      throw error;
    });
  };

  const { data, isLoading, error } = useQuery(
    ['getVendorAssignments', currentPage, searchValue, pageCount],
    fetchAssignments,
    {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const vendorOptions = useMemo(() => {
    return (vendorData?.data?.entities || []).map((vendor: any) => ({
      label: vendor.name,
      value: vendor.id,
    }));
  }, [vendorData]);

  const organizationOptions = useMemo(() => {
    return (organizationData?.data?.entities || []).map((org: any) => ({
      label: org.name,
      value: org.id,
    }));
  }, [organizationData]);
  const organizationMap = useMemo(() => {
    const entries = (organizationData?.data?.entities || []).map((org: any) => [org.id, org]);
    return new Map(entries) as Map<string, any>;
  }, [organizationData]);

  useEffect(() => {
    if (data) {
      setTotalEntities(data?.data?.totalEntities || 0);
    }
  }, [data]);

  useEffect(() => {
    setCurrentPage(0);
    setSearchValue('');
  }, [orgSelect]);

  const handleSearch = (value: any) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  const handleSelectOrganization = (option: any) => {
    setOrgSelect(option);
  };

  const handleAssign = () => {
    if (!vendorSelect || !orgSelect) {
      toast.error(String(t('vendorAssignmentPage.selectBoth')));
      return;
    }
    assignMutation.mutate(
      { vendorId: vendorSelect.value, organizationId: orgSelect.value },
      {
        onSuccess: () => {
          toast.success(String(t('vendorAssignmentPage.assignSuccess')));
          setVendorSelect(null);
          setOrgSelect(null);
          queryClient.invalidateQueries(['getVendorAssignments']);
        },
        onError: error => {
          const message: string = messageErrors(error, t);
          toast.error(message);
        },
      },
    );
  };

  const handleDelete = async (id: string) => {
    setIdDelete(id);
    setOpenModalConfirm(!openModalConfirm);
  };

  const onDelete = async (id: string) => {
    try {
      const response = await unassignVendor(id);
      if (response.status === 200) {
        await queryClient.invalidateQueries(['getVendorAssignments'], { refetchType: 'active' }, { cancelRefetch: true });
        const message: string = t('vendorAssignmentPage.unassignSuccess');
        toast.success(message);
        setOpenModalConfirm(!openModalConfirm);
      }
    } catch (error: any) {
      const message: string = messageErrors(error, t);
      toast.error(message);
    }
  };

  const assignMutation = useMutation(['assignVendorToOrg'], { mutationFn: assignVendorToOrg });

  const errorMessage = error ? messageErrors(error, t) : '';
  if (isLoading) {
    return (
      <div className="main-sub flex items-center justify-center py-10">
        <SpinnerComponent />
      </div>
    );
  }

  const assignments = data?.data?.entities || [];
  const showEmptyState = assignments.length === 0 && searchValue === '';
  const showNoResult = assignments.length === 0 && searchValue !== '';

  return (
    <>
      <div className="main-sub">
        <ActionTable
          placeholderSearch={t('vendorAssignmentPage.searchPlaceholder')}
          buttonName={t('vendorAssignmentPage.assign')}
          handleAddClick={handleAssign}
          handleSearch={handleSearch}
          searchValue={searchValue}
        />
        <div className="flex flex-col md:flex-row gap-4 px-6 pb-4">
          <Select
            placeholder={t('vendorAssignmentPage.selectVendor')}
            options={vendorOptions}
            value={vendorSelect}
            onChange={setVendorSelect}
            isHolderLabel={true}
          />
          <Select
            placeholder={t('vendorAssignmentPage.selectOrganization')}
            options={organizationOptions}
            value={orgSelect}
            onChange={handleSelectOrganization}
            isHolderLabel={true}
          />
        </div>
        {error ? (
          <EmptyState
            buttonName={t('vendorAssignmentPage.assign')}
            handleAddClick={handleAssign}
            title={t('vendorAssignmentPage.titleNoResult')}
            subtitle={errorMessage || t('vendorAssignmentPage.subTitleNoResult')}
            icon={assignmentEmpty}
          />
        ) : (
          <>
            {showEmptyState ? (
              <EmptyState
                buttonName={t('vendorAssignmentPage.assign')}
                handleAddClick={handleAssign}
                title={t('vendorAssignmentPage.titleNoResult')}
                subtitle={t('vendorAssignmentPage.subTitleNoResult')}
                icon={assignmentEmpty}
              />
            ) : showNoResult ? (
              <div className="p-[30px]">
                <NoResult />
              </div>
            ) : (
              <Table className="h-full overflow-y-scroll">
                <Table.Head className="text-xs text-gray-900 border-b-2 border-gray-100">
                  <Table.HeadCell className="bg-white font-medium text-xs px-3 w-30">
                    {t('vendorAssignmentPage.vendorName').toUpperCase()}
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-white font-medium text-xs px-3 w-30">
                    {t('vendorAssignmentPage.organizationName').toUpperCase()}
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-white font-medium text-xs px-3 w-30">
                    {t('vendorAssignmentPage.created').toUpperCase()}
                  </Table.HeadCell>
                  <Table.HeadCell className="w-6 bg-white">{''}</Table.HeadCell>
                </Table.Head>
                <Table.Body>
                  {assignments.map((item: any, index: number) => (
                    <Table.Row key={`row-${index + 1}`}
                      className="bg-white text-sm hover:bg-gray-50 border-b border-gray-100 cursor-pointer">
                      <Table.Cell className="font-semibold py-2.5 px-3">
                        <div className="text-secondary">{item?.name}</div>
                      </Table.Cell>
                      <Table.Cell className="py-2.5 px-3">
                        <div className="text-black">{item?.organization?.name || (organizationMap.get(item?.organizationId) as any)?.name}</div>
                      </Table.Cell>
                      <Table.Cell className="py-2.5 px-3">
                        <div className="text-black">{formatDateNotTime(item?.created || '')}</div>
                      </Table.Cell>
                      <Table.Cell className="py-2.5">
                        <GroupButtonWithIcons
                          className="items-center justify-center"
                          buttons={[
                            {
                              type: 'submit',
                              text: '',
                              classType: 'white',
                              icon: <HiOutlineTrash size={'20px'} />,
                              action: () => {
                                handleDelete(item?.id);
                              },
                            },
                          ]}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </>
        )}
        {isLoading && <SpinnerComponent />}
        <PaginateTable
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalEntities={totalEntities}
          isLoadingTable={isLoading}
        />
      </div>
      {openModalConfirm && (
        <ConfirmModal
          openModal={openModalConfirm}
          setOpenModal={setOpenModalConfirm}
          onDelete={() => onDelete(idDelete)}
        />
      )}
    </>
  );
};

export default VendorAssignment;
